import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { MultiSelectDialog } from "@/domains/common/components/molecules/MultiSelectDialog";

interface CategoryItem {
  id: number;
  label: string;
  description?: string;
  isSubcategory?: boolean;
}

const PipelinesTable = observer(() => {
  const { pipelinesStore, documentCategoriesStore } = useStores();
  const [pipelineCategories, setPipelineCategories] = useState<Record<number, number[]>>({});
  const [allCategoryItems, setAllCategoryItems] = useState<CategoryItem[]>([]);

  const loadDocumentCategories = async () => {
    try {
      // Load both top-level categories and subcategories in parallel
      const [topLevelCategories, subcategories] = await Promise.all([
        documentCategoriesStore.loadCategories().then(() => documentCategoriesStore.categoriesList),
        documentCategoriesStore.loadAllSubcategories(),
      ]);

      // Build a map of parent category names for subcategory labeling
      const parentNameMap = new Map<number, string>();
      topLevelCategories.forEach((cat) => {
        parentNameMap.set(cat.id, cat.category_name);
      });

      // Create combined list: top-level first, then subcategories with parent prefix
      const items: CategoryItem[] = [
        // Top-level categories
        ...topLevelCategories.map((c) => ({
          id: c.id,
          label: c.category_name,
          description: c.description || undefined,
          isSubcategory: false,
        })),
        // Subcategories with parent name prefix
        ...subcategories.map((c) => {
          let parentName = "";
          if (c.parent_category_id) {
            parentName = parentNameMap.get(c.parent_category_id) ?? "";
            if (!parentName) {
              console.warn(
                `Subcategory "${c.category_name}" (ID: ${c.id}) references missing parent category (ID: ${c.parent_category_id})`
              );
            }
          }
          return {
            id: c.id,
            label: parentName ? `${c.category_name} (${parentName})` : c.category_name,
            description: c.description || undefined,
            isSubcategory: true,
          };
        }),
      ];

      setAllCategoryItems(items);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to load document categories: ${message}`);
    }
  };

  const loadAllPipelineCategories = async () => {
    try {
      const pipelines = pipelinesStore.pipelinesList;
      const categoryMap: Record<number, number[]> = {};

      // Load categories for all pipelines in parallel
      await Promise.all(
        pipelines.map(async (pipeline) => {
          const categoryIds = await pipelinesStore.loadPipelineCategories(pipeline.id);
          categoryMap[pipeline.id] = categoryIds;
        })
      );

      setPipelineCategories(categoryMap);
    } catch (error) {
      toast.error("Failed to load pipeline categories");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await pipelinesStore.loadPipelines();
      await loadDocumentCategories();
      await loadAllPipelineCategories();
    };
    loadData();
  }, [pipelinesStore]);

  // Lazy load pipeline categories when popover opens (fallback)
  const handleLoadCategories = async (pipelineId: number) => {
    if (pipelineCategories[pipelineId] !== undefined) return; // Already loaded

    try {
      const categoryIds = await pipelinesStore.loadPipelineCategories(pipelineId);
      setPipelineCategories((prev) => ({
        ...prev,
        [pipelineId]: categoryIds,
      }));
    } catch (error) {
      toast.error("Failed to load pipeline categories");
    }
  };

  const handleCategoriesChange = async (pipelineId: number, categoryIds: number[]) => {
    try {
      // Ensure all IDs are numbers (in case MultiSelectDialog passes strings)
      const numericCategoryIds = categoryIds.map((id) => Number(id));
      await pipelinesStore.setPipelineCategories(pipelineId, numericCategoryIds);
      setPipelineCategories((prev) => ({
        ...prev,
        [pipelineId]: numericCategoryIds,
      }));
      await loadDocumentCategories();
      toast.success("Categories updated");
    } catch (error) {
      toast.error("Failed to update categories");
    }
  };

  if (pipelinesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (pipelinesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading pipelines: {pipelinesStore.error}
      </div>
    );
  }

  if (pipelinesStore.pipelinesList.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
        No pipelines found
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table className="w-full" style={{ tableLayout: "fixed" }}>
        <TableHeader>
          <TableRow>
            <TableHead style={{ width: "40%" }}>Name</TableHead>
            <TableHead style={{ width: "40%" }}>Slug</TableHead>
            <TableHead style={{ width: "20%" }}>Categories</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pipelinesStore.pipelinesList.map((pipeline) => (
            <TableRow key={pipeline.id}>
              <TableCell style={{ width: "40%" }}>{pipeline.name}</TableCell>
              <TableCell style={{ width: "40%" }}>{pipeline.slug}</TableCell>
              <TableCell style={{ width: "20%" }}>
                <MultiSelectDialog
                  items={allCategoryItems}
                  selectedIds={pipelineCategories[pipeline.id] || []}
                  onChange={(ids) => handleCategoriesChange(pipeline.id, ids as number[])}
                  onOpenChange={(id) => handleLoadCategories(id as number)}
                  resourceId={pipeline.id}
                  placeholder="No categories"
                  searchPlaceholder="Search categories..."
                  icon={<Tag className="h-4 w-4" />}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
});

export default PipelinesTable;
